import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { formatShipDate } from '../utils/dates'

// API function to fetch service options
const fetchServiceOptions = async (payload) => {
  const response = await fetch('/api/v6/service_options', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (!data.success) {
    throw new Error(data.error || 'Service options request failed');
  }

  return data;
};

// Transform leg data to service options payload
const transformLegToPayload = (legData, options = {}) => {
  const {
    brand_id = 'shipsticks',
    direction = 'outbound',
    handlingOption = 'pickup',
    combine = true,
    carrier = '',
    ...additionalOptions
  } = options;

  
  // Extract products from leg data
  const products = legData?.selectedProducts?.map(product => ({
    productId: product.id || product.product_id,
    quantity: 1 // Default to 1, actual quantity handled later
   })) || [];

  // Build ship route from leg addresses
  const shipRoute = {
    origin: transformAddress(legData?.fromAddress),
    destination: transformAddress(legData?.toAddress)
  };

  const arrivalDate = formatShipDate(legData?.arrivalDate || legData?.deliveryDate);

  return {
    service_options: {
      brand_id,
      arrivalDate: arrivalDate,
      direction,
      handlingOption,
      combine,
      carrier,
      products,
      shipRoute,
      ...additionalOptions
    }
  };
};

// Transform address object to API format
const transformAddress = (address) => {
  if (!address) return {};

  // Handle different address formats
  if (typeof address === 'string') {
    return { address1: address };
  }
  let portAddress = {};
  if (address.cruisePort) {
    portAddress = {
      address1: address.cruisePort.address.delivery_address_line || '',
      address2: address.cruisePort.address.delivery_address_line_1 || '',
      city: address.cruisePort.city || '',
      state: address.cruisePort.state || '',
      postalCode: address.cruisePort.zip || '',
    }
  }
  return {
    address1: address.name || address.address1 || address.street || address.address_1 || '',
    address2: '',
    city: address.city || '',
    state: address.state || address.region || '',
    countryCode: address.countryCode || address.country_code || 'US',
    postalCode: address.postalCode || address.zip || address.zip5 || '',
    ...portAddress
  };
};

// Main hook
export const useDeliverBy = (legData, options = {}) => {
  // in case of rapid changes to quantities, debounce them for 300ms
  // const [debouncedQuantities, setDebouncedQuantities] = useState({});

  const {
    enabled = true,
    refetchInterval,
    staleTime = 2 * 60 * 1000, // 2 minutes
    cacheTime = 5 * 60 * 1000, // 5 minutes
    ...queryOptions
  } = options;

  const debouncedQuantities = useMemo(() => {
    if (!legData?.selectedProducts) return {};
    return legData?.selectedProducts?.reduce((acc, product) => {
      acc[product.id || product.product_id] = product.quantity || 1;
      return acc;
    }, {});
  }, [legData?.selectedProducts]);

  // Create query key from leg data and options
  const deliveryDate = legData?.deliveryDate;
  const totalQuantity = Object.values(debouncedQuantities).reduce((sum, qty) => sum + qty, 0);

  const queryKey = useMemo(() => [
    'deliverBy',
    legData?.fromAddress?.place_id || legData?.fromAddress?.cruisePort?.city,
    legData?.toAddress?.place_id || legData?.toAddress?.cruisePort?.city,
    legData?.selectedProducts?.map(p => `${p.id || p.product_id}`).join(','),
    (deliveryDate instanceof Date ? deliveryDate.toLocaleDateString('en-US') : deliveryDate),
    options.direction,
    options.handlingOption,
    options.combine
  ],[legData, options]);

  const fromAddress = legData?.fromAddress;
  const toAddress = legData?.toAddress;

  const apiEnabled = enabled && totalQuantity > 0 && !!legData?.deliveryDate &&
         !!(fromAddress?.country_code || fromAddress?.cruisePort?.id) &&
         !!(toAddress?.country_code || toAddress?.cruisePort?.city) &&
         !!legData?.selectedProducts?.length;

  const {
    data,
    isLoading,
    error,
    refetch,
    isError,
    isFetching,
    isStale,
    dataUpdatedAt,
  } = useQuery(
    queryKey,
    () => {
      const payload = transformLegToPayload(legData, options);
      return fetchServiceOptions(payload);
    },
    {
      enabled: apiEnabled,
      staleTime,
      cacheTime,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchInterval,
      ...queryOptions,
    }
  );

  // Memoized service options calculation to prevent unnecessary recalculations
  const serviceOptions = useMemo(() => {
    if (!data?.services || data.services.length === 0) {
      return [];
    }
    return data?.services.map(service => {
      // Apply debounced quantities to service_options and calculate new totals
      const updatedServiceOptions = service.service_options.map(serviceOption => {
        const productId = serviceOption.product._id;
        const debouncedQuantity = debouncedQuantities[productId] || 0;
        const unitPrice = serviceOption.service_rate?.price_cents || 0; 
      
        // remove pennies from unit price to match pricing api
        return {
          ...serviceOption,
          quantity: debouncedQuantity,
          price_cents: unitPrice,
          total_price_cents: unitPrice * debouncedQuantity,
          adjusted_price_cents: 0,
          total_adjusted_price_cents: 0
        };
      });

      // Calculate overall service totals by summing all service options
      const totalServicePriceCents = updatedServiceOptions.reduce((sum, so) => 
        sum + so.total_price_cents, 0
      );
      
      const totalServiceQuantity = updatedServiceOptions.reduce((sum, so) => 
        sum + so.quantity, 0
      );

      return {
        ...service,
        service_type: 'deliver_by',
        service_options: updatedServiceOptions,
        price_cents: totalServicePriceCents,
        combined_price_cents: totalServicePriceCents,
        package_count: totalServiceQuantity,
        adjusted_price_cents: 0,
        combined_adjusted_price_cents: 0
      };
    }) || [];
  }, [data?.services, debouncedQuantities]);
  
  const combinedServices = data?.combined_services || null;

  // Helper functions
  const getServiceById = (serviceId) => {
    return serviceOptions.find(service => service.id === serviceId);
  };

  const getServicesByCarrier = (carrierName) => {
    return serviceOptions.filter(service => service.carrier_name === carrierName);
  };

  const getCheapestService = () => {
    if (!serviceOptions.length) return null;
    return serviceOptions.reduce((cheapest, current) => 
      current.combined_adjusted_price_cents < cheapest.combined_adjusted_price_cents ? current : cheapest
    );
  };

  const getFastestService = () => {
    if (!serviceOptions.length) return null;
    return serviceOptions.reduce((fastest, current) => 
      current.transit_days < fastest.transit_days ? current : fastest
    );
  };

  const formatPrice = (priceCents) => {
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  return {
    // Data
    serviceOptions,
    combinedServices,
    
    // Loading states
    isLoading,
    isFetching,
    isStale,
    isError,
    error: error?.message || null,
    dataUpdatedAt,
    
    // Actions
    refetch,
    
    // Helper functions
    getServiceById,
    getServicesByCarrier,
    getCheapestService,
    getFastestService,
    formatPrice,
    
    // Computed states
    isEmpty: serviceOptions.length === 0,
    hasServices: serviceOptions.length > 0,
    serviceCount: serviceOptions.length,
    
    // Raw response for debugging
    rawResponse: data,
  };
};